import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { fetchCrmTodaySummary, type CrmTodaySummary } from "../api";
import { supabase } from "../supabase";

type Platform = "videolink" | "crm" | "avatar";

const PLATFORMS: { id: Platform; label: string }[] = [
  { id: "videolink", label: "VideoLink" },
  { id: "crm", label: "CRM" },
  { id: "avatar", label: "Avatar video" },
];

const CRM_NAV: { to: string; label: string; badgeKey: string | null }[] = [
  { to: "/admin/crm", label: "Aujourd'hui", badgeKey: "today" },
  { to: "/admin/crm/estrellas", label: "Estrellas", badgeKey: "estrellas" },
  { to: "/admin/crm/touchpoints", label: "Touchpoints", badgeKey: "touchpoints" },
  { to: "/admin/crm/batches", label: "Batches", badgeKey: "batches" },
  { to: "/admin/crm/leads", label: "Leads", badgeKey: "leads" },
  { to: "/admin/crm/traces", label: "Traces", badgeKey: null },
  { to: "/admin/crm/suggestions", label: "Suggestions", badgeKey: "suggestions" },
  { to: "/admin/crm/exited", label: "Sortis", badgeKey: "exited" },
  { to: "/admin/crm/settings", label: "Réglages", badgeKey: null },
];

function formatBadge(key: string, n: number): string {
  if (key === "estrellas") return `${n} ★`;
  return String(n);
}

export default function AdminCrmLayout() {
  const nav = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [summary, setSummary] = useState<CrmTodaySummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const reloadSummary = useCallback(() => {
    fetchCrmTodaySummary()
      .then((s) => {
        setSummary(s);
        setSummaryError(null);
      })
      .catch((e: Error) => {
        setSummary(null);
        setSummaryError(e.message || "Erreur API");
      });
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav("/admin/login", { replace: true });
      else setUserEmail(data.session.user.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session) nav("/admin/login", { replace: true });
      else setUserEmail(session.user.email ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, [nav]);

  useEffect(() => {
    reloadSummary();
  }, [reloadSummary]);

  const selectPlatform = (p: Platform) => {
    if (p === "crm") return;
    if (p === "videolink") nav("/admin?app=videolink", { replace: true });
    else nav("/admin?app=avatar", { replace: true });
  };

  const logout = async () => {
    await supabase.auth.signOut();
    nav("/admin/login", { replace: true });
  };

  const badges = summary?.sidebar_badges ?? {};

  return (
    <div className="flex min-h-screen flex-col bg-ivory">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-stone bg-white px-4 py-3">
        <nav className="flex flex-wrap gap-1">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPlatform(p.id)}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                p.id === "crm"
                  ? "bg-charcoal text-ivory"
                  : "text-concrete hover:bg-ivory hover:text-charcoal"
              }`}
            >
              {p.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3 text-sm">
          {userEmail && (
            <span className="hidden truncate text-concrete sm:inline max-w-[200px]">
              {userEmail}
            </span>
          )}
          <button
            type="button"
            onClick={logout}
            className="whitespace-nowrap text-concrete hover:text-charcoal"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="flex w-full shrink-0 flex-col border-b border-stone bg-black text-ivory md:w-[240px] md:border-b-0 md:border-r md:border-stone">
          <div className="border-b border-[#2a2a2a] px-4 py-5">
            <div className="font-sans text-xl font-extrabold tracking-tight">
              LLYLL<span className="text-lemonade">.</span>
            </div>
            <span className="mt-1 block font-mono text-[10px] text-ash">CRM · admin</span>
          </div>

          <div className="px-3 py-3">
            <input
              type="search"
              readOnly
              placeholder="Chercher un lead…"
              className="w-full cursor-not-allowed rounded-md border border-[#333] bg-[#1a1a1a] px-3 py-2 text-sm text-ivory opacity-70 placeholder:text-ash"
              aria-disabled
            />
          </div>

          <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-4">
            {CRM_NAV.map((item) => {
              const n =
                item.badgeKey != null ? (badges[item.badgeKey] ?? 0) : 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/admin/crm"}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm font-medium transition ${
                      isActive
                        ? "bg-lemonade font-semibold text-black"
                        : "text-ivory hover:bg-[#1a1a1a]"
                    }`
                  }
                >
                  <span className="flex-1">{item.label}</span>
                  {item.badgeKey != null && (
                    <span
                      className={`rounded-full px-1.5 py-0 font-mono text-[11px] font-semibold ${
                        n === 0 ? "bg-ash text-black" : "bg-[#333] text-lemonade"
                      }`}
                    >
                      {formatBadge(item.badgeKey, n)}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {userEmail && (
            <div className="mt-auto border-t border-[#2a2a2a] px-4 py-3 font-mono text-[10px] text-ash">
              <div className="text-ivory">{userEmail}</div>
            </div>
          )}
        </aside>

        <main className="min-w-0 flex-1 overflow-auto p-4 md:p-8">
          <Outlet
            context={{
              summary,
              summaryError,
              reloadSummary,
            }}
          />
        </main>
      </div>
    </div>
  );
}
